import {
  AppBox,
  AppDateAndTime,
  AppDatePicker,
  AppGridBox,
  AppSelect,
  AppSkeletonMatchCardList,
  AppText,
  AppTitle,
} from "../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../hooks/app";
import { useEffect, useState } from "react";
import { StatusEnum } from "../../../commons/enums/status-enum";
import { ISelectOptions } from "../../../commons/components/ui-components/Select";
import { SortOrderEnum } from "../../../commons/enums/sort-order-enum";
import { formatDateTime } from "../../../commons/utilities/date-utils";
import { fetchManageTeamCompetitionList } from "../../../redux/slices/football/admin/team/manage-team-competitions-slice";
import ManageMatchCard from "./components/ManageMatchCard";
import { findIndexByCondition } from "../../../commons/utilities/array-utils";
import {
  cleanUpManageTeamMatchList,
  fetchManageTeamMatchList,
  IFootballManageTeamMatchOption,
} from "../../../redux/slices/football/admin/team/manage-team-matches-slice";
import TeamCompetitionInvite from "./components/TeamCompetitionInvite";
import { IFootballSubTeamDetail } from "../../../commons/models/football/interface/i-football-sub-team";

export default function TeamManageEvents() {
  const dispatch = useAppDispatch();
  const { subTeamInfoStatus } = useAppSelector((state) => state.footballManageSubTeam);
  const { teamInfoStatus } = useAppSelector((state) => state.footballManageTeam);
  const { matchList, liveMatches, upcomingMatches, finishedMatches, matchStatus } = useAppSelector(
    (state) => state.footballManageTeamMatchListSlice
  );
  const { fetchedTeam } = useAppSelector((state) => state.fetchedTeam);
  const { teamCompetitionList } = useAppSelector((state) => state.footballManageTeamCompetitions);
  const [matchStartDate, setMatchStartDate] = useState<Date | null>(null);
  const [matchEndDate, setMatchEndDate] = useState<Date | null>(null);
  const [competitionSelectOptions, setCompetitionSelectOptions] = useState<ISelectOptions[]>([]);
  const [seasonSelectOptions, setSeasonSelectOptions] = useState<ISelectOptions[]>([]);
  const [currentCompetitionId, setCurrentCompetitionId] = useState<number>();
  const [currentSeasonId, setCurrentSeasonId] = useState<number>();
  const [currentCompetitionOption, setCurrentCompetitionOption] = useState<ISelectOptions>();
  const [currentSeasonOption, setCurrentSeasonOption] = useState<ISelectOptions>();

  const onChangeMatchDate = (
    dates: Date | [Date | null, Date | null] | null,
    event: React.SyntheticEvent<any, Event>
  ) => {
    event.preventDefault();
    if (dates instanceof Array) {
      const [start, end] = dates;
      setMatchStartDate(start || null);
      setMatchEndDate(end || null);
    }
  };

  useEffect(() => {
    if (fetchedTeam.id) {
      dispatch(
        fetchManageTeamCompetitionList({
          teamId: fetchedTeam.id,
          urlParams: {
            hostTeamId: (fetchedTeam as IFootballSubTeamDetail).parent?.id,
          },
        })
      );
    }
  }, [fetchedTeam]);

  useEffect(() => {
    const competitionOptions: ISelectOptions[] = [];

    if (teamCompetitionList.length > 0) {
      teamCompetitionList.forEach((competition) => {
        competitionOptions.push({
          title: competition.title,
          value: competition.id,
        });
      });
      const currentTeamCompetitionId = fetchedTeam.latestSeason?.competition?.id;
      let currentTeamCompetitionIndex = findIndexByCondition(
        competitionOptions,
        (option) => option.value === currentTeamCompetitionId
      );
      if (currentTeamCompetitionIndex >= 0) {
        setCurrentCompetitionOption(competitionOptions[currentTeamCompetitionIndex]);
        setCurrentCompetitionId(Number(competitionOptions[currentTeamCompetitionIndex].value));
      } else {
        setCurrentCompetitionId(Number(competitionOptions[0].value));
      }
      setCompetitionSelectOptions(competitionOptions);
    }
  }, [teamCompetitionList]);

  useEffect(() => {
    if (currentCompetitionId) {
      const currentCompetition = teamCompetitionList.first((competition) => competition.id === currentCompetitionId);
      const seasonOptions: ISelectOptions[] = [];
      currentCompetition?.seasons?.forEach((season) => {
        seasonOptions.push({ title: season?.title, value: season?.id });
      });
      const currentTeamSeasonId = fetchedTeam.latestSeason?.season?.id;
      let currentTeamSeasonIndex = findIndexByCondition(
        seasonOptions,
        (option) => option.value === currentTeamSeasonId
      );
      if (currentTeamSeasonIndex >= 0) {
        setCurrentSeasonOption(seasonOptions[currentTeamSeasonIndex]);
        setCurrentSeasonId(Number(seasonOptions[currentTeamSeasonIndex].value));
      } else {
        setCurrentSeasonOption(seasonOptions[0]);
        setCurrentSeasonId(Number(seasonOptions[0].value));
      }
      setSeasonSelectOptions(seasonOptions);
    }
  }, [currentCompetitionId]);

  useEffect(() => {
    if (fetchedTeam.id && currentCompetitionId && currentSeasonId) {
      fetchMatches();
    } else {
      dispatch(cleanUpManageTeamMatchList());
    }
  }, [currentCompetitionId, currentSeasonId, fetchedTeam, matchEndDate]);

  const fetchMatches = () => {
    let matchesOption: IFootballManageTeamMatchOption = {
      teamId: fetchedTeam.id,
      urlParams: {
        hostTeamId: (fetchedTeam as IFootballSubTeamDetail).parent?.id,
        competitionId: currentCompetitionId,
        seasonId: currentSeasonId,
        sort: "scheduledAt",
        sortOrder: SortOrderEnum.Descending,
        page: 0,
        beforeDate: matchStartDate ? formatDateTime(matchStartDate, "YYYY-MM-DD HH:mm:00") : undefined,
        afterDate: matchEndDate ? formatDateTime(matchEndDate, "YYYY-MM-DD HH:mm:00") : undefined,
      },
    };
    dispatch(fetchManageTeamMatchList(matchesOption));
  };

  useEffect(() => {
    if (fetchedTeam.id && currentCompetitionId && currentSeasonId) {
      let intervalId: NodeJS.Timeout;
      intervalId = setInterval(() => {
        fetchMatches();
      }, 30000);
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [matchList]);

  return (
    <AppBox mb="sm" displayBlock={true}>
      <AppBox flexDirection="column" gap="md">
        <TeamCompetitionInvite />
        <AppBox flexDirection="column" gap="sm">
          {(teamInfoStatus === StatusEnum.Succeeded || subTeamInfoStatus === StatusEnum.Succeeded) && (
            <AppBox flexDirection="column" gap="xs">
              <AppBox justifyContent="space-between">
                <AppTitle as="h5">Matches & Result</AppTitle>
                <AppBox>
                  <AppDatePicker
                    onChange={onChangeMatchDate}
                    selectedStart={matchStartDate}
                    selectedEnd={matchEndDate}
                    isMultiSelector
                    isIconButton
                    isClearable
                    calenderPlacement="left-start"
                  />
                </AppBox>
              </AppBox>
              {competitionSelectOptions.length > 0 && (
                <AppGridBox columns={2} gap="sm">
                  {competitionSelectOptions.length > 0 && (
                    <AppSelect
                      currentOption={currentCompetitionOption}
                      options={competitionSelectOptions}
                      onChange={(option) => {
                        setCurrentCompetitionId(Number(option.value));
                      }}
                    />
                  )}
                  {seasonSelectOptions.length > 0 && (
                    <AppSelect
                      currentOption={currentSeasonOption}
                      options={seasonSelectOptions}
                      onChange={(option) => {
                        setCurrentSeasonId(Number(option.value));
                      }}
                    />
                  )}
                </AppGridBox>
              )}
            </AppBox>
          )}
          {matchStatus === StatusEnum.Loading ? (
            <AppSkeletonMatchCardList />
          ) : (
            <>
              {matchStatus === StatusEnum.Succeeded && matchList.length === 0 ? (
                <AppText>There are not any matches for this team.</AppText>
              ) : (
                <>
                  {liveMatches.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Live Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {liveMatches.mapArray((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                  {upcomingMatches.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Upcoming Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {upcomingMatches.mapArray((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                  {finishedMatches.length > 0 && (
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Previous Matches</AppTitle>
                      <AppBox flexDirection="column" gap="sm">
                        {finishedMatches.mapArray((match, index) => (
                          <AppBox flexDirection="column" gap="2xs" key={index}>
                            <AppDateAndTime date={match.scheduledAt} />
                            <AppBox flexDirection="column" gap="sm">
                              <ManageMatchCard match={match} />
                            </AppBox>
                          </AppBox>
                        ))}
                      </AppBox>
                    </AppBox>
                  )}
                </>
              )}
            </>
          )}
          {competitionSelectOptions.length === 0 && <AppText>There are not any matches for this team.</AppText>}
        </AppBox>
      </AppBox>
    </AppBox>
  );
}
